import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listMyBookings, AGREEMENT_LABEL,
  type AgreementRow, type AgreementStatus,
} from '../../lib/artist';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDate } from '../../lib/format';
import './ArtistPages.css';

/**
 * Every agreement, grouped by status.
 *
 * The status set and the labels come from the organizer side (task 31) rather
 * than being redefined here, so one agreement never reads differently to the two
 * parties looking at it. Note there is no 'declined': an artist declining a
 * request sets 'cancelled', which is why that group is labelled for both.
 *
 * Agency-routed requests are INCLUDED. The engine does not hide them from the
 * artist — listAgreementsForUser has no routing filter and respondToAgreement
 * checks only profile ownership — so filtering them out here would show the
 * artist less than they actually control.
 */

const GROUPS: { status: AgreementStatus; note: string }[] = [
  { status: 'pending', note: 'Sent to you, not yet fully signed.' },
  { status: 'signed', note: 'Both parties signed. Deposit release is next.' },
  { status: 'deposit_paid', note: 'Deposit released to you.' },
  { status: 'performed', note: 'Performed and settled in full.' },
  { status: 'cancelled', note: 'Declined by you, or cancelled by the organizer. Voleto has one state for both.' },
  { status: 'disputed', note: 'Under review.' },
];

function Row({ a }: { a: AgreementRow }) {
  const needsYou = a.status === 'pending' && !a.artist_signed_at;
  return (
    <article className={`art-card${needsYou ? ' art-card--action' : ''}${a.status === 'cancelled' ? ' art-card--muted' : ''}`}>
      <div className="art-row">
        <div className="art-row__main">
          <span className="art-row__title">{a.event_title}</span>
          <span className="art-row__meta">
            {a.event_date ? formatDate(a.event_date) : 'Date TBC'}
            {a.performance_start_time ? ` · ${a.performance_start_time}` : ''}
            {a.set_duration_minutes ? ` · ${a.set_duration_minutes} min` : ''}
          </span>
          <span className="art-hint mono">{a.contract_number}</span>
        </div>
        <div className="art-row__side">
          <span className="art-money">{formatMoney(a.performance_fee_cents)}</span>
          <span className="art-hint">
            {a.artist_signed_at ? '✓ you signed' : 'you: unsigned'}
            {' · '}
            {a.organizer_signed_at ? '✓ organizer signed' : 'organizer: unsigned'}
          </span>
          <Link className="button button--secondary" to={`/artist/bookings/${a.id}`}>
            {needsYou ? 'Review request' : 'View'}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function ArtistBookings() {
  const [rows, setRows] = useState<AgreementRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listMyBookings()
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your bookings.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="art-status">Loading…</p>;
  if (error) return <p className="art-status art-error" role="alert">{error}</p>;

  const all = rows ?? [];

  return (
    <div className="art-page">
      <header className="art-header">
        <div>
          <h1 className="art-title">Bookings</h1>
          <p className="art-lede">Every agreement you are a party to, grouped by where it stands.</p>
        </div>
      </header>

      {all.length === 0 && (
        <p className="art-status art-muted">
          No booking requests yet. Organizers can only send requests to approved profiles.
        </p>
      )}

      {GROUPS.map(({ status, note }) => {
        const group = all.filter((a) => a.status === status);
        if (group.length === 0) return null;
        return (
          <section key={status} className="art-section">
            <div className="art-section__head">
              <h2 className="art-section__title">
                {AGREEMENT_LABEL[status]}{' '}
                <span className="art-hint mono">({group.length})</span>
              </h2>
            </div>
            <p className="art-section__note">{note}</p>
            <div className="art-list">
              {group.map((a) => <Row key={a.id} a={a} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
